import { useState, type ReactNode } from 'react';
import {
  Button,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Select,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { formatPesos, parsePesos, percentToBasisPoints } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';
import { planColumns } from './columns.tsx';
import type { PlanRow } from './types.ts';
import { SETTLEMENT_LABEL, cadenceKey } from './vocabulary.tsx';

interface Brand {
  id: string;
  name: string;
}

type Kind = 'FLAT' | 'TIERED' | 'RETAINER';

/**
 * The plan book, and the form that writes to it.
 *
 * WHAT THIS REPLACES. The list behind this dialog used to be a
 * `GROUP BY (tier, commission_rate_bp)` over the agents themselves, so every
 * row was a rate somebody happened to carry — no name, no settlement rule, no
 * carry-over term, no way to hold the design's tiered band or its fixed
 * retainer, and a plan nobody was on could not appear at all. "New plan" was
 * inert because there was nothing to write to.
 *
 * THE FORM'S SHAPE IS THE MODEL'S SHAPE. A plan's revenue share is a set of
 * BANDS, and a flat plan is the degenerate case of one band starting at zero.
 * So the kind selector does not switch between three different forms; it
 * decides how many band rows there are and whether a retainer is asked for.
 * That is deliberate: three separate forms is three separate places for the
 * arithmetic to diverge from `agent.assert_plan_shape`.
 */
export function CommissionPlansDialog({
  open,
  onClose,
  plans,
  plansFailed,
  brands,
  brandId,
  canWrite,
  measuredCadence,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  plans: PlanRow[];
  plansFailed: boolean;
  brands: Brand[];
  brandId: string | null;
  canWrite: boolean;
  /** The cadence the platform's periods actually run at, or null. */
  measuredCadence: string | null;
  onChanged: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [mode, setMode] = useState<'list' | 'new'>('list');
  const [archiving, setArchiving] = useState<PlanRow | null>(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [archivingId, setArchivingId] = useState<string | null>(null);

  const measured = cadenceKey(measuredCadence);

  const close = () => {
    setMode('list');
    setArchiving(null);
    onClose();
  };

  const archive = async () => {
    if (!archiving) return;
    setArchivingId(archiving.id);
    try {
      await api.post(`/api/admin/agents/plans/${archiving.id}/archive`, {
        reason: archiveReason.trim(),
      });
      toast(`${archiving.ref} withdrawn — it stays on the agents that were settled under it.`);
      setArchiving(null);
      setArchiveReason('');
      onChanged();
    } catch (error: unknown) {
      showError({
        title: `${archiving.ref} was not withdrawn`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was changed. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setArchivingId(null);
    }
  };

  let body: ReactNode;
  if (mode === 'new') {
    body = (
      <NewPlanForm
        brands={brands}
        brandId={brandId}
        measuredCadence={measuredCadence}
        onCancel={() => setMode('list')}
        onCreated={(name) => {
          toast(`${name} created. Assign it to an agent from the agent’s drawer.`);
          setMode('list');
          onChanged();
        }}
      />
    );
  } else if (plansFailed) {
    body = (
      <EmptyState icon="warning-circle" title="The commission plans could not be read">
        The commission plans could not be fetched, so this list is empty
        because nothing was read — not because no plan exists. Close and reopen this dialog after
        reloading the screen to ask again.
      </EmptyState>
    );
  } else if (plans.length === 0) {
    body = (
      <EmptyState icon="percent" title="No commission plans yet">
        No plan has been written for {brandId ? 'this brand' : 'any brand'}. Until one is,
        every agent carries a rate agreed by hand and there is no settlement rule or carry-over
        term recorded against it.
      </EmptyState>
    );
  } else {
    body = (
      <>
        <DataTable
          caption="Commission plans, with what each pays, how it settles, how long it carries a loss, and how many agents are on it"
          columns={planColumns({
            measuredSettlement: measured,
            canWrite,
            archiving: archivingId,
            onArchive: (p) => {
              setArchiving(p);
              setArchiveReason('');
            },
          })}
          rows={plans}
          rowKey={(p) => p.id}
        />
        <div className="text-subtle" style={{ fontSize: '11px', marginTop: 'var(--space-2)' }}>
          <strong>Agents</strong> counts the agents whose STANDING assignment is that plan — who is
          on it today, not everyone who has ever been. A plan with none is a plan waiting to be
          used, which is a row the old rate-grouping could not produce at all.
        </div>
      </>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={close}
        title="Agent commission plans"
        wide
        actions={
          <>
            <Button onClick={mode === 'new' ? () => setMode('list') : close}>
              {mode === 'new' ? 'Back to plans' : 'Close'}
            </Button>
            {mode === 'list' && canWrite && (
              <Button variant="primary" onClick={() => setMode('new')}>
                <Icon name="plus" />
                New plan
              </Button>
            )}
          </>
        }
      >
        {mode === 'list' && (
          <p className="text-muted" style={{ fontSize: '13px' }}>
            A plan is assigned per agent from the agent&rsquo;s own drawer, and the change applies
            from the next settlement period — never retroactively. A plan agents are standing on
            cannot be withdrawn until they are moved.
          </p>
        )}

        {body}

        {mode === 'list' && !canWrite && (
          <div className="notice notice-quiet">
            <Icon name="lock-key" />
            <div>
              Writing a plan needs the <strong>partners.manage</strong> capability, which your role
              does not hold. A plan decides what the platform pays a partner, so it sits behind the
              capability that governs partners rather than the one that releases a player&rsquo;s
              withdrawal.
            </div>
          </div>
        )}
      </Dialog>

      {/* Withdrawing is a decision with a reason, so it is its own dialog
          rather than a confirm() — and the reason is what the audit row
          carries.

          NOT DISMISSIBLE, AND NOT BECAUSE A WRITE IS IN FLIGHT. The press is
          already guarded on that side: the confirm button disables itself while
          `archivingId` is set. The moment that needed guarding is the one
          BEFORE the press, when an operator has typed out a reason somebody
          will read in the audit trail later and a stray Escape — or a click
          landing just outside this panel — throws the whole decision away with
          no trace that it was ever made. Cancel is a deliberate button, so the
          way out stays one click; it just stops being an accident. */}
      <Dialog
        open={archiving !== null}
        onClose={() => setArchiving(null)}
        title={archiving ? `Withdraw ${archiving.name}` : 'Withdraw plan'}
        dismissible={false}
        actions={
          <>
            <Button onClick={() => setArchiving(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={archiveReason.trim().length < 8 || archivingId !== null}
              onClick={() => void archive()}
            >
              {archivingId ? 'Withdrawing…' : 'Withdraw the plan'}
            </Button>
          </>
        }
      >
        {/* WARN RATHER THAN DANGER, AND THE REASONING IS WORTH WRITING DOWN.
            The tone contract sitting beside `.notice-warn` in the design system
            keeps danger for the irreversible and for anything that moves money
            or a reported figure. Withdrawing a plan does neither. No settled
            period is recomputed, nothing is removed, no mail goes out, and the
            platform refuses the press outright while any agent is still
            standing on the plan — so by the time it succeeds, nobody's terms
            have moved and no partner's own portal reads any differently.

            What it DOES do is close a one-way door on this particular plan:
            there is no control in this console, or anywhere behind it, that
            un-withdraws one. The way back is to write the same terms again as a
            new plan, which is a recoverable consequence, and recoverable is
            what warn is for. If withdrawing a plan wore the same red as
            refusing a player's withdrawal, the red would stop meaning anything.

            The prose below was already here as a muted paragraph under the
            title. It has been MOVED, not copied — same sentence, same wording —
            because what it says is exactly what the notice exists to say, and
            two copies of it would be one copy too many to keep true. */}
        <div className="notice notice-warn">
          <Icon name="warning" />
          <div>
            The plan is <strong>archived, not deleted</strong>. It stops being offered for a new
            assignment and keeps answering for every settled period computed under it — no record
            in this platform is removed. Nothing can un-withdraw it afterwards: if these terms are
            wanted again, they have to be written as a new plan. No partner is told.
          </div>
        </div>
        <Field label="Reason" hint="At least 8 characters. It goes into the audit trail.">
          {({ id, describedBy }) => (
            <textarea
              id={id}
              className="input"
              rows={3}
              aria-describedby={describedBy}
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="Superseded by the 2027 network terms"
            />
          )}
        </Field>
      </Dialog>
    </>
  );
}

/* ── The form ──────────────────────────────────────────────────────────── */

interface BandDraft {
  /** A peso amount as typed. Parsed to centavos on submit. */
  threshold: string;
  /** A percentage as typed. Converted to basis points on submit. */
  rate: string;
}

function NewPlanForm({
  brands,
  brandId,
  measuredCadence,
  onCancel,
  onCreated,
}: {
  brands: Brand[];
  brandId: string | null;
  measuredCadence: string | null;
  onCancel: () => void;
  onCreated: (name: string) => void;
}) {
  const { showError } = useErrorDialog();
  const [name, setName] = useState('');
  const [brand, setBrand] = useState(brandId ?? brands[0]?.id ?? '');
  const [tier, setTier] = useState<'MASTER' | 'SUB_AGENT' | 'ANY'>('MASTER');
  const [kind, setKind] = useState<Kind>('FLAT');
  const [settlement, setSettlement] = useState<'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY'>(
    cadenceKey(measuredCadence) === 'FORTNIGHTLY'
      ? 'FORTNIGHTLY'
      : cadenceKey(measuredCadence) === 'MONTHLY'
        ? 'MONTHLY'
        : 'WEEKLY',
  );
  const [carryMonths, setCarryMonths] = useState('3');
  const [retainer, setRetainer] = useState('');
  const [bands, setBands] = useState<BandDraft[]>([
    { threshold: '0', rate: '' },
    { threshold: '', rate: '' },
  ]);
  const [note, setNote] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  /** How many band rows this kind uses. A flat or retainer plan has one. */
  const bandCount = kind === 'TIERED' ? bands.length : 1;

  const setBand = (index: number, patch: Partial<BandDraft>) =>
    setBands((cur) => cur.map((b, i) => (i === index ? { ...b, ...patch } : b)));

  const submit = async () => {
    const problems: Record<string, string> = {};
    if (name.trim().length < 3) problems['name'] = 'Give the plan a name operators will recognise.';
    if (!brand) problems['brandId'] = 'Choose the brand this plan is offered on.';

    const months = Number(carryMonths);
    if (!Number.isInteger(months) || months < 0 || months > 60) {
      problems['carryOverMonths'] = 'A carry-over term is a whole number of months, 0 to 60.';
    }

    let retainerCentavos = 0n;
    if (kind === 'RETAINER') {
      try {
        retainerCentavos = parsePesos(retainer);
        if (retainerCentavos <= 0n) {
          problems['retainerAmount'] = 'A retainer plan needs an amount above zero.';
        }
      } catch {
        problems['retainerAmount'] = 'A peso amount, e.g. 25000 or 25,000.00';
      }
    }

    const wire: { minNetRevenue: string; rateBp: number }[] = [];
    for (let i = 0; i < bandCount; i++) {
      const draft = bands[i]!;
      const rateText = draft.rate.trim();
      if (!/^\d+(\.\d{1,2})?$/.test(rateText)) {
        problems[`band-${i}-rate`] = 'A percentage, e.g. 4 or 7.5.';
        continue;
      }
      const rateBp = percentToBasisPoints(Number(rateText));
      if (rateBp > 10_000) {
        problems[`band-${i}-rate`] = 'A rate cannot exceed 100%.';
        continue;
      }
      if (i === 0) {
        wire.push({ minNetRevenue: '0', rateBp });
        continue;
      }
      try {
        const threshold = parsePesos(draft.threshold);
        if (threshold <= 0n) {
          problems[`band-${i}-threshold`] = 'A band above the first starts above ₱0.';
          continue;
        }
        wire.push({ minNetRevenue: threshold.toString(), rateBp });
      } catch {
        problems[`band-${i}-threshold`] = 'A peso amount, e.g. 10000000 or 10,000,000.00';
      }
    }

    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }

    setSaving(true);
    setFields({});
    try {
      await api.post('/api/admin/agents/plans', {
        name: name.trim(),
        brandId: brand,
        tier,
        kind,
        settlement,
        retainerAmount: retainerCentavos.toString(),
        carryOverMonths: months,
        bands: wire,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onCreated(name.trim());
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: 'The plan was not created',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was saved. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stack">
      <Field label="Plan name" error={fields['name']}>
        {({ id, describedBy }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Master growth tier"
          />
        )}
      </Field>

      <Field
        label="Brand"
        error={fields['brandId']}
        hint="Plans are brand-scoped: the terms one brand offers its network are not another's."
      >
        {({ id, describedBy }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
          >
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field label="Applies to">
        {({ id }) => (
          <Select
            id={id}
            value={tier}
            onChange={(e) => setTier(e.target.value as 'MASTER' | 'SUB_AGENT' | 'ANY')}
          >
            <option value="MASTER">Master agents</option>
            <option value="SUB_AGENT">Sub-agents</option>
            <option value="ANY">Either tier</option>
          </Select>
        )}
      </Field>

      <Field
        label="Kind"
        hint={
          kind === 'TIERED'
            ? 'Bands are WHOLE-AMOUNT: crossing a threshold re-prices the entire period, it does not price only the excess.'
            : kind === 'RETAINER'
              ? 'A fixed amount every settlement period, whatever the revenue did, on top of the share below.'
              : 'One rate on all net GGR.'
        }
      >
        {({ id, describedBy }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            value={kind}
            onChange={(e) => setKind(e.target.value as Kind)}
          >
            <option value="FLAT">Flat rate</option>
            <option value="TIERED">Tiered band</option>
            <option value="RETAINER">Retainer</option>
          </Select>
        )}
      </Field>

      {kind === 'RETAINER' && (
        <Field
          label="Retainer per period"
          error={fields['retainerAmount']}
          hint="In pesos. Stored as centavos — ₱25,000.00 is 2500000."
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              inputMode="decimal"
              value={retainer}
              onChange={(e) => setRetainer(e.target.value)}
              placeholder="25000"
            />
          )}
        </Field>
      )}

      <div className="panel-title">{kind === 'TIERED' ? 'Bands' : 'Rate'}</div>
      {fields['bands'] && (
        <span className="field-error" role="alert">
          {fields['bands']}
        </span>
      )}

      {bands.slice(0, bandCount).map((band, i) => (
        <div className="row" key={i}>
          {i > 0 && (
            <Field
              label={`Band ${i + 1} from`}
              error={fields[`band-${i}-threshold`]}
              hint="Net GGR in the period, in pesos."
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  inputMode="decimal"
                  value={band.threshold}
                  onChange={(e) => setBand(i, { threshold: e.target.value })}
                  placeholder="10000000"
                />
              )}
            </Field>
          )}
          <Field
            label={i === 0 ? (kind === 'TIERED' ? 'Entry rate' : 'Rate') : `Band ${i + 1} rate`}
            error={fields[`band-${i}-rate`]}
            hint={i === 0 && kind === 'TIERED' ? 'What the agent earns below the first threshold.' : undefined}
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                inputMode="decimal"
                value={band.rate}
                onChange={(e) => setBand(i, { rate: e.target.value })}
                placeholder={i === 0 ? '4.0' : '7.0'}
              />
            )}
          </Field>
        </div>
      ))}

      {kind === 'TIERED' && (
        <div className="row">
          <Button
            small
            disabled={bands.length >= 8}
            onClick={() => setBands((cur) => [...cur, { threshold: '', rate: '' }])}
          >
            <Icon name="plus" />
            Add a band
          </Button>
          {bands.length > 2 && (
            <Button small onClick={() => setBands((cur) => cur.slice(0, -1))}>
              Remove the last band
            </Button>
          )}
        </div>
      )}

      <Field
        label="Settlement"
        hint={
          measuredCadence && cadenceKey(measuredCadence) !== settlement
            ? `The platform's periods actually run ${measuredCadence.toLowerCase()}. A plan that promises a different cadence is a promise nobody is keeping.`
            : 'How often a period under this plan settles.'
        }
      >
        {({ id, describedBy }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            value={settlement}
            onChange={(e) =>
              setSettlement(e.target.value as 'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY')
            }
          >
            {Object.entries(SETTLEMENT_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field
        label="Negative carry-over"
        error={fields['carryOverMonths']}
        hint="Months a losing period is carried before it is written off. 0 carries it until it clears."
      >
        {({ id, describedBy }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            inputMode="numeric"
            value={carryMonths}
            onChange={(e) => setCarryMonths(e.target.value)}
            placeholder="3"
          />
        )}
      </Field>

      <Field label="Note" hint="Optional. What this plan is for, in a sentence.">
        {({ id, describedBy }) => (
          <textarea
            id={id}
            className="input"
            rows={2}
            aria-describedby={describedBy}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        )}
      </Field>

      {kind === 'RETAINER' && retainer.trim() !== '' && (
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            An agent on this plan is paid{' '}
            <strong>
              {(() => {
                try {
                  return formatPesos(parsePesos(retainer));
                } catch {
                  return '—';
                }
              })()}
            </strong>{' '}
            every {SETTLEMENT_LABEL[settlement]?.toLowerCase().replace('ly', '')} period on top of
            the share above, whatever the revenue did.
          </div>
        </div>
      )}

      <div className="row">
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="primary" disabled={saving} onClick={() => void submit()}>
          {saving ? 'Creating…' : 'Create plan'}
        </Button>
      </div>
    </div>
  );
}
